import {useIonAlert, useIonLoading, useIonToast} from "@ionic/react";
import {alertCircle, checkmarkCircle} from "ionicons/icons";
import {Haptics, NotificationType} from "@capacitor/haptics";
import {
    useReceptionDoorSlideOpenMutation,
    useReceptionDoorUnlockMutation
} from "../query/accessControlMutations";
import IWBrokerClient from "../client/IWBrokerClient";

function escapeHTML(value: string) {
    return value.replace(/[&<>'"]/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
    })[character] ?? character);
}

function useUX() {
    const [presentToast] = useIonToast();
    const [presentAlert] = useIonAlert();
    const [present] = useIonLoading();
    const receptionDoorUnlockMutation = useReceptionDoorUnlockMutation();
    const receptionDoorSlideOpenMutation = useReceptionDoorSlideOpenMutation();

    async function revealExteriorGatePin() {
        try {
            const {pin} = await IWBrokerClient.request('exterior gate pin') as {pin: string};

            return presentAlert({
                header: 'Exterior Gates',
                subHeader: 'Padlocks / Keypad',
                message: `PIN: <strong>${escapeHTML(pin)}</strong>`,
                buttons: ['OK'],
            });
        } catch (error: unknown) {
            await presentError(error);
        }
    }

    async function presentError(error: unknown) {
        const message = typeof error === 'object'
            && error !== null
            && 'message' in error
            && typeof error.message === 'string'
            ? error.message
            : 'Operation Failed';

        Haptics.notification({type: NotificationType.Error}).catch(e => e);
        await presentToast({
            message,
            duration: 2500,
            color: 'danger',
            icon: alertCircle
        });
    }

    async function presentResult(fn: Function, successMessage?: string) {
        try {
            await fn();

            Haptics.notification({type: NotificationType.Success}).catch(e => e);
            await presentToast({
                message: successMessage ?? 'Operation Successful',
                duration: 2500,
                color: 'success',
                icon: checkmarkCircle
            });
        } catch (error: unknown) {
            await presentError(error);
        }
    }

    async function receptionDoorUnlock() {
        try {
            await receptionDoorUnlockMutation.mutateAsync();

            Haptics.notification({type: NotificationType.Success}).catch(e => e);

            await present({
                message: '<strong>Within 10 seconds:</strong><br /><br />Turn the handle slightly to the right then swiftly to the left.',
                duration: 10000,
                backdropDismiss: true,
                spinner: 'dots',
            });
        } catch (error: unknown) {
            await presentError(error);
        }
    }

    async function receptionDoorSlideOpen() {
        await presentResult(async () => {
            await receptionDoorSlideOpenMutation.mutateAsync();
        }, "Opening Sliding Door");
    }

    return {
        presentResult,
        presentError,
        revealExteriorGatePin,
        receptionDoorSlideOpen,
        requestingDoorSlide: receptionDoorSlideOpenMutation.isPending,
        receptionDoorUnlock,
        requestingDoorUnlock: receptionDoorUnlockMutation.isPending
    }
}

export default useUX;
